import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";

import { cn } from "@/design-system/cn.ts";

/**
 * A bottom sheet that rests at two heights before it closes.
 *
 * Phones are short, and a card that opens at full height buries the map it was
 * opened from. So this arrives just tall enough to answer the question that made
 * you tap — and only grows if you ask it to. The three states form a ladder
 * rather than a switch:
 *
 *   rest ──drag up──▶ full ──drag down / scroll back to top──▶ rest ──flick down──▶ closed
 *
 * Closing is deliberately only reachable from `rest`. Reading to the end of a
 * long card and carrying on downward should hand back the map, not throw the
 * card away — dismissal has to be its own gesture, made at a point where you can
 * already see the map behind it.
 *
 * The resting height is *measured*, not a fraction: the caller marks the last
 * element worth seeing at a glance with `data-sheet-fold`, and the sheet opens
 * to exactly there. A fraction would be right for one listing and wrong for the
 * next, since travel times are a variable number of rows.
 *
 * Scrolling is locked while resting. The sheet is always FULL_VH tall and slid
 * down to reveal the fold, so the rest of it genuinely sits below the screen;
 * letting the content scroll there would push it into that dead space. Locked,
 * the same upward drag that would have scrolled instead promotes to `full` —
 * which is where scrolling belongs anyway.
 */

const FULL_VH = 75;
/** Breathing room under the fold, so it doesn't sit flush with the screen edge. */
const FOLD_PAD = 12;
/** Drag past this (px) to change detent. Roughly a deliberate thumb flick. */
const THRESHOLD = 56;
/** Travel before a body stroke commits to being the sheet's rather than the carousel's. */
const LOCK_PX = 10;
/**
 * Downward speed, in px/ms, that closes the sheet outright however far it got.
 *
 * The ladder is about where a *considered* drag ends up; a flick is not that. It
 * says "get rid of this" in one motion, and having to land on rest and then
 * flick a second time to finish the job is the kind of thing you notice every
 * single time. High enough that an unhurried drag still steps down to rest.
 */
const FLICK_V = 0.7;
/**
 * Window, in ms, the release velocity is measured over.
 *
 * Not the last move pair: one delayed frame — a slow render, a busy main thread
 * — makes a decisive flick measure as a crawl, and the sheet stops on the middle
 * rung for no reason the user can see. Averaging over a window also keeps the
 * intent right at the other end, since someone who drags fast and then holds
 * still before letting go has decided *not* to flick, and the stationary tail
 * pulls the average back down on its own.
 */
const FLICK_WINDOW_MS = 120;
/** How far the content must have scrolled before returning to the top collapses it. */
const ARM_SCROLL = 24;
const EASE = "transform 260ms cubic-bezier(0.22, 0.61, 0.36, 1)";

type Detent = "rest" | "full";

export function DetentSheet({
  onDismiss,
  /** Changing this re-measures and returns to `rest` — a new subject starts over. */
  resetKey,
  children,
  className,
}: {
  onDismiss: () => void;
  resetKey?: string;
  children: React.ReactNode;
  className?: string;
}) {
  const [detent, setDetent] = useState<Detent>("rest");
  const [drag, setDrag] = useState(0);
  const [dragging, setDragging] = useState(false);
  const [leaving, setLeaving] = useState(false);
  /** Height of the sheet that shows while resting, measured from the fold. */
  const [restH, setRestH] = useState(0);
  /** Whether the content actually overflows the sheet at full height. */
  const [scrollable, setScrollable] = useState(false);
  /**
   * False for the first painted frame, so the sheet has somewhere to slide up
   * *from*. Without it the first paint lands at `translateY(0)` — the ref isn't
   * attached yet, so the measured height is 0 and the resting offset computes to
   * nothing — and the sheet appears fully open in the middle of the screen and
   * then slides *down* to rest, which reads as a glitch rather than an entrance.
   */
  const [entered, setEntered] = useState(false);

  const sheetRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const handleRef = useRef<HTMLButtonElement>(null);
  const start = useRef<{
    x: number;
    y: number;
    pointer: number;
    claimed: boolean;
    /** Recent positions, trimmed to FLICK_WINDOW_MS, for the release velocity. */
    samples: { y: number; t: number }[];
  } | null>(null);
  /** True once the content has scrolled far enough that returning to the top
   *  reads as a deliberate move back rather than as never having left. */
  const armed = useRef(false);
  /**
   * Whether the next transform change is a *move* — and so worth animating — or
   * a correction, which is not.
   *
   * The resting offset is measured, and the measurement can change after the
   * sheet is already open: webfonts land (`display=swap`), a photo decodes, a
   * travel row appears. Each of those moves the fold, and animating the
   * correction plays it as a deliberate glide — the card appears, then visibly
   * jolts to a new height for no reason the user can connect to anything they
   * did. Corrections are applied instantly instead, so they read as the layout
   * settling rather than as the sheet moving.
   */
  const animated = useRef(false);
  /** Last measured resting height, so a re-measure that changes nothing costs nothing. */
  const lastRestH = useRef(-1);

  // ── measurement ───────────────────────────────────────────────────────────
  useLayoutEffect(() => {
    const measure = () => {
      const sheet = sheetRef.current;
      const scroller = scrollRef.current;
      if (!sheet || !scroller) return;

      const sheetH = sheet.offsetHeight;
      setScrollable(scroller.scrollHeight > scroller.clientHeight + 1);
      const fold = scroller.querySelector("[data-sheet-fold]");
      if (!fold) {
        setRestH(sheetH * 0.66);
        return;
      }
      // Distance from the top of the scrollable content to the bottom of the
      // fold, independent of how far it happens to be scrolled right now.
      const offset =
        fold.getBoundingClientRect().bottom -
        scroller.getBoundingClientRect().top +
        scroller.scrollTop;
      const handleH = handleRef.current?.offsetHeight ?? 0;
      const next = Math.min(sheetH, handleH + offset + FOLD_PAD);
      if (next === lastRestH.current) return;

      // A *later* measurement that moves the fold — webfonts landing, a photo
      // decoding — is a correction, and applying it instantly is what keeps it
      // from being seen as the sheet jolting. The first one is not a correction:
      // it is the entrance, and it still glides.
      if (lastRestH.current !== -1) animated.current = false;
      lastRestH.current = next;
      setRestH(next);
    };

    lastRestH.current = -1;
    measure();
    setDetent("rest");
    setDrag(0);
    armed.current = false;
    scrollRef.current?.scrollTo({ top: 0 });
    // Webfonts load with `display=swap`, so text reflows after first paint and
    // moves the fold with it. Re-measuring then is what keeps the sheet's height
    // right; doing it as a correction is what keeps it from being seen.
    void document.fonts?.ready.then(measure).catch(() => {});

    // Photos load late and travel rows expand, either of which moves the fold.
    const scroller = scrollRef.current;
    if (!scroller) return;
    const ro = new ResizeObserver(measure);
    ro.observe(scroller);
    for (const child of scroller.children) ro.observe(child);
    window.addEventListener("resize", measure);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [resetKey]);

  // One frame at translateY(100%), then the real offset — which is what turns
  // the entrance into a slide instead of a jump. rAF rather than a layout
  // effect: a layout effect would land in the same paint and there would be no
  // frame to transition from.
  useEffect(() => {
    setEntered(false);
    // A new subject cancels a dismissal in flight. `leaving` pins the sheet
    // off-screen, and a selection change reconciles this same instance rather
    // than remounting it — so without this, tapping another pin during the ~260ms
    // dismissal left the new card invisible, and the old transition then landed
    // and cleared the new selection. The tap read as doing nothing at all.
    // Clearing the flag also disarms that stale transitionend, since the handler
    // below only dismisses while `leaving`.
    setLeaving(false);
    const id = requestAnimationFrame(() => {
      animated.current = true;
      setEntered(true);
    });
    return () => cancelAnimationFrame(id);
  }, [resetKey]);

  const height = () => sheetRef.current?.offsetHeight ?? 0;
  const restingY = useCallback(
    (d: Detent) => (d === "full" ? 0 : Math.max(0, height() - restH)),
    [restH],
  );

  const settle = useCallback((next: Detent) => {
    animated.current = true;
    setDetent(next);
    setDrag(0);
    armed.current = false;
    if (next === "rest") scrollRef.current?.scrollTo({ top: 0 });
  }, []);

  const leave = useCallback(() => {
    animated.current = true;
    setDragging(false);
    setLeaving(true);
  }, []);

  // ── drag ──────────────────────────────────────────────────────────────────
  /**
   * The sheet owns vertical strokes; anything else inside it keeps its own.
   *
   * A body drag is therefore *provisional* until it has travelled far enough to
   * show a direction. A horizontal one is released — that is the photo carousel,
   * which shares this surface — and only a vertical one is claimed. Claiming on
   * contact instead is what broke paging through photos: the sheet took every
   * stroke that started on the card.
   *
   * Nothing captures the pointer except the handle. Capture retargets the whole
   * event stream to the capturing element, so a capture here would cut the
   * carousel — a descendant — out of its own gesture entirely, which is the
   * same bug by another route.
   */
  const onPointerDown = (e: React.PointerEvent, fromHandle: boolean) => {
    if (leaving) return;
    // A second finger is a pinch, not a drag. Without this it overwrote the
    // tracked pointer wholesale, and since the id checks below then follow the
    // *new* finger, its half of the spread was read as a clean solo drag —
    // so pinching to zoom a photo threw the card away instead.
    if (!e.isPrimary) {
      start.current = null;
      return;
    }
    // Mid-scroll, a body stroke belongs to the scroller. Only once it is back at
    // the top does dragging down mean "collapse" rather than "keep reading".
    if (!fromHandle && detent === "full" && (scrollRef.current?.scrollTop ?? 0) > 0) return;

    start.current = {
      x: e.clientX,
      y: e.clientY,
      pointer: e.pointerId,
      claimed: fromHandle,
      samples: [{ y: e.clientY, t: e.timeStamp }],
    };
    if (!fromHandle) return;

    setDragging(true);
    // A dismissing flick ends with the finger past the bottom of the screen and
    // off the handle. Capture keeps the events coming, so it doesn't freeze.
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };

  const onPointerMove = (e: React.PointerEvent) => {
    const from = start.current;
    if (!from || from.pointer !== e.pointerId) return;

    const dx = e.clientX - from.x;
    const dy = e.clientY - from.y;

    if (!from.claimed) {
      if (Math.hypot(dx, dy) < LOCK_PX) return;
      // Sideways is the carousel's, and at `full` so is upward — that one is
      // the scroller reading further down the card.
      if (Math.abs(dx) > Math.abs(dy) || (detent === "full" && dy < 0)) {
        start.current = null;
        return;
      }
      from.claimed = true;
      setDragging(true);
    }

    from.samples.push({ y: e.clientY, t: e.timeStamp });
    while (from.samples.length > 2 && e.timeStamp - from.samples[0]!.t > FLICK_WINDOW_MS) {
      from.samples.shift();
    }

    // At `full` the only way out is downward — upward is already the top.
    const limited = detent === "full" ? Math.max(0, dy) : dy;
    setDrag(Math.max(-restingY(detent), limited));
  };

  /**
   * Decided from the pointer's own travel, not from the tracked `drag` state.
   * State is one render behind whenever the moves and the release land in a
   * single batch, and a gesture that resolves by however React happened to
   * schedule is a gesture that sometimes does nothing.
   */
  const onPointerUp = (e: React.PointerEvent) => {
    const from = start.current;
    if (!from || from.pointer !== e.pointerId) return;
    start.current = null;
    if (!from.claimed) return;
    // Settling out of a drag — snap-back included — is a move, not a correction.
    animated.current = true;
    setDragging(false);

    const dy = e.clientY - from.y;
    const oldest = from.samples[0]!;
    const span = e.timeStamp - oldest.t;
    const flicked = span > 0 && (e.clientY - oldest.y) / span >= FLICK_V;

    if (detent === "rest") {
      if (dy <= -THRESHOLD) settle("full");
      else if (dy >= THRESHOLD || (flicked && dy > 0)) leave();
      else setDrag(0);
      return;
    }

    // From `full`, one gesture can go all the way. Dragging past where `rest`
    // sits — or flicking, which says the same thing faster — closes outright,
    // rather than parking on the middle rung and making you start again.
    if (dy >= restingY("rest") + THRESHOLD || (flicked && dy >= THRESHOLD)) leave();
    else if (dy >= THRESHOLD) settle("rest");
    else setDrag(0);
  };

  /** A cancelled pointer made no decision, so it snaps back rather than acting. */
  const onPointerCancel = (e: React.PointerEvent) => {
    const from = start.current;
    if (!from || from.pointer !== e.pointerId) return;
    start.current = null;
    setDragging(false);
    setDrag(0);
  };

  // ── scroll ────────────────────────────────────────────────────────────────
  // Reading back up to the top of a fully-open card is asking for the map again.
  const onScroll = () => {
    const el = scrollRef.current;
    if (!el || detent !== "full" || dragging) return;
    if (el.scrollTop > ARM_SCROLL) armed.current = true;
    else if (armed.current && el.scrollTop <= 0) settle("rest");
  };

  // `100%` rather than a measured pixel offset, because this also covers the
  // first frame — before the ref exists and any measurement would read 0.
  const offscreen = leaving || !entered;

  return (
    <div
      ref={sheetRef}
      className={cn("fixed inset-x-0 bottom-0 z-40 flex flex-col", className)}
      style={{
        height: `${FULL_VH}dvh`,
        transform: offscreen ? "translateY(100%)" : `translateY(${restingY(detent) + drag}px)`,
        transition: dragging || !animated.current ? "none" : EASE,
      }}
      onPointerDown={(e) => onPointerDown(e, false)}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerCancel}
      onTransitionEnd={(e) => {
        // `transitionend` bubbles, and the photo strip inside animates
        // `transform` too — so paging a photo and then dismissing within its
        // 320ms would fire this early and cut the exit short. Only this
        // element's own transition ends the dismissal.
        if (e.target !== e.currentTarget) return;
        if (leaving && e.propertyName === "transform") onDismiss();
      }}
    >
      <div className="console console-grid flex min-h-0 flex-1 flex-col overflow-hidden rounded-t-2xl text-chalk">
        {/* The handle drags in every state, so it owns the gesture outright —
            `touch-action: none` is what stops Android reading a downward flick
            here as pull-to-refresh and cancelling the pointer mid-drag. */}
        <button
          ref={handleRef}
          type="button"
          onPointerDown={(e) => {
            // The root's own handler is upstream of this one and would re-record
            // the same pointer as an unclaimed body drag, throwing away the fact
            // that it started on the handle.
            e.stopPropagation();
            onPointerDown(e, true);
          }}
          onClick={() => (detent === "full" ? settle("rest") : leave())}
          aria-label={detent === "full" ? "Collapse listing" : "Close listing"}
          aria-expanded={detent === "full"}
          className="flex w-full shrink-0 cursor-grab touch-none items-center justify-center px-4 py-2.5 active:cursor-grabbing"
        >
          <span className="h-1.5 w-11 rounded-full bg-chalk-mute/70 transition-colors" />
        </button>

        <div
          ref={scrollRef}
          onScroll={onScroll}
          className={cn(
            "min-h-0 flex-1",
            // `pan-y` ONLY when there is genuinely something to scroll.
            //
            // `touch-action` is a static property, so it has to be decided ahead
            // of the gesture, and `pan-y` hands every vertical drag to the
            // browser — which then cancels our pointer stream. On a card that
            // fits, that meant dragging down on the price or the travel rows did
            // nothing at all, while the same drag on the photo worked, because
            // the gallery is `touch-none`. Nothing to scroll, nothing to hand
            // over; the sheet keeps the gesture and collapses.
            //
            // `contain` keeps a scroll that runs out of content from chaining to
            // the page behind it — on Android, pull-to-refresh again.
            detent === "full" && scrollable
              ? "touch-pan-y overflow-y-auto overscroll-contain"
              : "touch-none overflow-hidden",
          )}
          style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
        >
          {children}
        </div>
      </div>
    </div>
  );
}
